import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  Typography,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";

import { observer } from "mobx-react";
import emissionViewStore from "../../../Store/marco/emissionViewStore.js";
import { getConstByStandard } from "../../utils/getConstFileByStandard.js";
import { useTranslation } from "react-i18next";

import { CustomTextField } from "../CustomTextField.js";
import { CustomDatePicker } from "../CustomDatePicker.js";
import { DateRangePicker } from "../DateRangePicker.js";
import {
  DATA_ENTRY_CALCULATION_IDS,
  formatWeightDistance,
  hasNumericValue,
  isValidNonNegativeNumber,
} from "../../utils/dataEntryCalculation.js";

const INITIAL_CAT_FIELDS = {
  weight: "",
  distance: "",
  origin: "",
  destination: "",
  distance_3_3: "",
  passengers: 1,
  tripTypeValue: 1,
  numberOfTrips: 1,
};

const INITIAL_ERRORS = {
  usedDate: false,
  consumptionValue: false,
  dateRange: false,
  weight: false,
  distance: false,
  distance_3_3: false,
  passengers: false,
  numberOfTrips: false,
  tripTypeValue: false,
};

const DataEntryEditModal = observer(() => {
  const store = emissionViewStore;
  const CONST = getConstByStandard(store.standardId);
  const { t } = useTranslation("ActivityMain");

  // ✅ 衍生值提到最上面，hooks 之前
  const category = CONST.category;
  const scope = category[store.selectedTab]?.split("_")[0];
  const cat = category[store.selectedTab]?.substring(3);

  // ── state ──────────────────────────────────────
  const [usedDate, setUsedDate] = useState(null);
  const [consumptionValue, setConsumptionValue] = useState("");
  const [confirmedRange, setConfirmedRange] = useState(null);
  const [catFields, setCatFields] = useState(INITIAL_CAT_FIELDS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isHelpText, setIsHelpText] = useState(false);
  const [errors, setErrors] = useState(INITIAL_ERRORS);

  // ── helpers ─────────────────────────────────────
  const setCatField = (field, value) =>
    setCatFields((prev) => ({ ...prev, [field]: value }));

  const clearError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: false }));

  const calcCat3xConsumption = (overrides = {}) => {
    const fields = { ...catFields, ...overrides };
    const base =
      fields.distance_3_3 * fields.numberOfTrips * fields.tripTypeValue;
    return device?.calculationId === 4 ? base * fields.passengers : base;
  };

  const resetForm = () => {
    setUsedDate(null);
    setConsumptionValue("");
    setConfirmedRange(null);
    setCatFields(INITIAL_CAT_FIELDS);
    setErrors(INITIAL_ERRORS);
    setIsHelpText(false);
    setIsDisabled(false);
  };

  // ── useEffect ───────────────────────────────────
  useEffect(() => {
    if (!store.showDataEntryEditModal) {
      resetForm();
      return;
    }

    const device = store.selectedDataEntry;
    const dataItem = store.selectedDataEntryItem;
    if (!device || !dataItem) return;

    // 帶入日期
    if (device.dataTypeId === 2) {
      setUsedDate(dayjs().year(store.selectedYear));
    } else if (device.dataTypeId === 4) {
      if (
        typeof dataItem.usedDate === "string" &&
        dataItem.usedDate.includes("_")
      ) {
        const [startStr, endStr] = dataItem.usedDate.split("_");
        const startDate = new Date(startStr);
        const endDate = new Date(endStr);
        if (!isNaN(startDate) && !isNaN(endDate)) {
          setConfirmedRange({ startDate, endDate, key: "selection" });
        }
      }
    } else {
      setUsedDate(dataItem.usedDate || "");
    }

    const initialFields = {
      weight: dataItem.weight ?? "",
      distance: dataItem.distance ?? "",
      origin: dataItem.origin ?? "",
      destination: dataItem.destination ?? "",
      // ✅ cat 3_x 的距離也存在 distance 欄位，帶入 distance_3_3
      distance_3_3: dataItem.distance ?? "",
      passengers: dataItem.passengerCount ?? "",
      tripTypeValue: dataItem.tripType ?? 1,
      numberOfTrips: dataItem.trips ?? 1,
    };
    setCatFields(initialFields);

    const isBasicCategory = ["3_1", "3_2"].includes(cat);
    const isWeightDistanceMode =
      isBasicCategory &&
      device.calculationId === DATA_ENTRY_CALCULATION_IDS.WEIGHT_DISTANCE;
    const isDistanceMode =
      isBasicCategory &&
      device.calculationId === DATA_ENTRY_CALCULATION_IDS.DISTANCE;
    const isFuelMode =
      device.calculationId === DATA_ENTRY_CALCULATION_IDS.FUEL;

    setConsumptionValue(
      isWeightDistanceMode
        ? formatWeightDistance(initialFields.weight, initialFields.distance)
        : isDistanceMode
          ? initialFields.distance
          : isFuelMode
            ? initialFields.weight
            : (dataItem.convertedOperationValue ?? ""),
    );
  }, [store.showDataEntryEditModal]);

  // ── 保護：store 還沒準備好時不渲染 ───────────────
  if (!store.selectedDataEntry || !store.selectedDataEntryItem) return null;

  // ✅ if 保護之後才解構，安全讀取
  const device = store.selectedDataEntry;
  const dataItem = store.selectedDataEntryItem;

  const isCat3basic = ["3_1", "3_2"].includes(cat);
  const isCat3x = ["3_3", "3_4", "3_5"].includes(cat);
  const isWeightDistanceBased =
    isCat3basic &&
    device.calculationId === DATA_ENTRY_CALCULATION_IDS.WEIGHT_DISTANCE;
  const isDistanceBased =
    isCat3basic &&
    device.calculationId === DATA_ENTRY_CALCULATION_IDS.DISTANCE;
  const isFuelBased =
    device.calculationId === DATA_ENTRY_CALCULATION_IDS.FUEL;

  // passengers-km 取 km 單位（Edit 不需要 unitOptions，直接用 dataUnitName）
  const extractedUnit =
    (device.dataUnitId === 17 || device.dataUnitId === 18) &&
    device.dataUnitName?.includes("-")
      ? device.dataUnitName.split("-").pop()
      : device.dataUnitName;

  //tonne-km get tonne and km
  const [weightUnit, distanceUnit] = isCat3basic
    ? (device.dataUnitName ?? "").split("-")
    : [];

  // ── 月份重複檢查 ─────────────────────────────────
  const isMonthDataExists = (deviceData, selectedDate) => {
    if (!deviceData?.data || !Array.isArray(deviceData.data)) return false;
    const formattedMonth = dayjs(selectedDate).format("YYYY-MM");
    return deviceData.data.some((timeData) =>
      timeData.datas?.some((d) => {
        if (d.id === dataItem.id) return false; // ✅ 排除自己
        return dayjs(d.usedDate).format("YYYY-MM") === formattedMonth;
      }),
    );
  };

  const handleDateChange = (selectedDate) => {
    if (!selectedDate) return;

    if (device.dataTypeId === 1) {
      const currentDevice = store.dataEntryListData?.fullList.find(
        (d) => d.id === device.id,
      );
      if (currentDevice && isMonthDataExists(currentDevice, selectedDate)) {
        setIsHelpText(true);
        setIsDisabled(true);
        return;
      }
    }

    setUsedDate(selectedDate);
    setIsHelpText(false);
    setIsDisabled(false);
    clearError("usedDate");
  };

  // ── 驗證 ─────────────────────────────────────────
  const buildValidationErrors = () => {
    const calcId = device.calculationId;
    const isCat3xCalc2or4 = isCat3x && (calcId === 2 || calcId === 4);
    const requiresManualConsumption =
      isFuelBased ||
      !(isWeightDistanceBased || isDistanceBased || isCat3x);
    return {
      usedDate: device.dataTypeId !== 4 && !usedDate,
      dateRange:
        device.dataTypeId === 4 &&
        (!confirmedRange?.startDate || !confirmedRange?.endDate),
      consumptionValue:
        requiresManualConsumption &&
        !isValidNonNegativeNumber(consumptionValue),
      weight:
        isWeightDistanceBased &&
        !isValidNonNegativeNumber(catFields.weight),
      distance:
        (isWeightDistanceBased || isDistanceBased) &&
        !isValidNonNegativeNumber(catFields.distance),
      tripTypeValue: isCat3xCalc2or4 && !catFields.tripTypeValue,
      numberOfTrips: isCat3xCalc2or4 && !catFields.numberOfTrips,
      distance_3_3:
        isCat3xCalc2or4 &&
        (catFields.distance_3_3 === "" || Number(catFields.distance_3_3) < 0),
      passengers: isCat3x && calcId === 4 && !catFields.passengers,
    };
  };

  // ── 儲存 ─────────────────────────────────────────
  const handleSave = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const newErrors = buildValidationErrors();
      setErrors(newErrors);
      if (Object.values(newErrors).some(Boolean)) return;

      if (device.dataTypeId === 1) {
        const currentDevice = store.dataEntryListData?.fullList.find(
          (d) => d.id === device.id,
        );
        if (currentDevice && isMonthDataExists(currentDevice, usedDate)) {
          setIsHelpText(true);
          setIsDisabled(true);
          return;
        }
      }

      const editSuccess = await store.editDE(
        usedDate,
        confirmedRange,
        consumptionValue,
        catFields,
        isCat3x,
        isCat3basic,
      );
      if (!editSuccess) return;

      await store.getDEListData({ keepPage: true });

      store._handleCloseDataEntryEditModal();
    } catch (error) {
      console.error("Error in edit operation", error);
    } finally {
      setIsSubmitting(false);
      setIsHelpText(false);
    }
  };

  // ── JSX ──────────────────────────────────────────
  return (
    <Dialog
      open={store.showDataEntryEditModal}
      onClose={() => store._handleCloseDataEntryEditModal()}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <IconButton
          onClick={() => store._handleCloseDataEntryEditModal()}
          sx={{
            position: "absolute",
            right: 8,
            top: 10,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        {/* ✅ 改從 device 讀取 */}
        {t("Edit Consumption Data For") + " " + device.itemName}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid
          container
          spacing={2}
          justifyContent="flex-start"
          alignItems="flex-start"
          rowSpacing={1}
        >
          {/* cat 3_3 / 3_4 / 3_5 / 3_1 / 3_2：出發地 / 目的地 */}
          {(isCat3x || isCat3basic) && (
            <>
              <Grid size={6}>
                <CustomTextField
                  label={t("Origin")}
                  placeholder="Origin"
                  value={catFields.origin}
                  type="text"
                  handleChange={(e) => setCatField("origin", e.target.value)}
                  disabled={
                    isCat3basic &&
                    device.isStatic &&
                    !isFuelBased &&
                    !isDistanceBased
                  }
                />
              </Grid>
              <Grid size={6}>
                <CustomTextField
                  label={t("Destination")}
                  placeholder="Destination"
                  type="text"
                  value={catFields.destination}
                  handleChange={(e) =>
                    setCatField("destination", e.target.value)
                  }
                  disabled={
                    isCat3basic &&
                    device.isStatic &&
                    !isFuelBased &&
                    !isDistanceBased
                  }
                />
              </Grid>
            </>
          )}

          {/* cat 3_3/3_4/3_5 calculationId === 2 or 4 */}
          {isCat3x && device.calculationId !== 3 && (
            <>
              <Grid
                size={12}
                sx={{ backgroundColor: "#f2f2f2", p: 2, borderRadius: "4px" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="subtitle1">
                    <span style={{ color: "red" }}>*</span> {t("Trip Type")}
                  </Typography>
                  <RadioGroup
                    row
                    value={catFields.tripTypeValue}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setCatField("tripTypeValue", v);
                      setConsumptionValue(
                        calcCat3xConsumption({ tripTypeValue: v }),
                      );
                    }}
                  >
                    <FormControlLabel
                      value={1}
                      control={<Radio />}
                      label={t("One Way")}
                    />
                    <FormControlLabel
                      value={2}
                      control={<Radio />}
                      label={t("Round Trip")}
                    />
                  </RadioGroup>
                </Box>
              </Grid>
              {/* 行程次數 */}
              <Grid size={6}>
                <CustomTextField
                  required
                  label={t("Number of Trips")}
                  type="number"
                  unit="times"
                  min={1}
                  placeholder="Enter Number of Trips"
                  value={catFields.numberOfTrips}
                  handleChange={(e) => {
                    const v = e.target.value;
                    setCatField("numberOfTrips", v);
                    clearError("numberOfTrips");
                    setConsumptionValue(
                      calcCat3xConsumption({ numberOfTrips: v }),
                    );
                  }}
                  error={errors.numberOfTrips}
                />
              </Grid>
              {/* 距離 */}
              <Grid size={6}>
                <CustomTextField
                  required
                  label={t("Distance")}
                  type="number"
                  unit={extractedUnit}
                  value={catFields.distance_3_3}
                  placeholder="Enter Distance"
                  handleChange={(e) => {
                    const v = e.target.value;
                    setCatField("distance_3_3", v);
                    clearError("distance_3_3");
                    setConsumptionValue(
                      calcCat3xConsumption({ distance_3_3: v }),
                    );
                  }}
                  error={errors.distance_3_3}
                />
              </Grid>
              {/* 乘客人數 */}
              {device.calculationId === 4 && (
                <Grid size={6}>
                  <CustomTextField
                    required
                    label={t("Number of Passengers")}
                    type="number"
                    unit="pax"
                    min={1}
                    placeholder="Enter Passenger"
                    value={catFields.passengers}
                    handleChange={(e) => {
                      const v = e.target.value;
                      setCatField("passengers", v);
                      clearError("passengers");
                      setConsumptionValue(
                        calcCat3xConsumption({ passengers: v }),
                      );
                    }}
                    error={errors.passengers}
                  />
                </Grid>
              )}
            </>
          )}
          {/* cat 3_1 / 3_2：依計算模式顯示 weight / distance */}
          {(isWeightDistanceBased || isDistanceBased) && (
            <>
              {isWeightDistanceBased && (
                <Grid size={6}>
                  <CustomTextField
                    required
                    label={t("Weight")}
                    value={catFields.weight}
                    unit={t(weightUnit) || ""}
                    handleChange={(e) => {
                      const v = e.target.value;
                      setCatField("weight", v);
                      clearError("weight");
                      setConsumptionValue(
                        formatWeightDistance(v, catFields.distance),
                      );
                    }}
                    error={errors.weight}
                  />
                </Grid>
              )}
              <Grid size={6}>
                <CustomTextField
                  required
                  label={t("Distance")}
                  value={catFields.distance}
                  unit={
                    isWeightDistanceBased
                      ? t(distanceUnit) || ""
                      : t(device.dataUnitName) || ""
                  }
                  handleChange={(e) => {
                    const v = e.target.value;
                    setCatField("distance", v);
                    clearError("distance");
                    setConsumptionValue(
                      isWeightDistanceBased
                        ? formatWeightDistance(catFields.weight, v)
                        : v,
                    );
                  }}
                  error={errors.distance}
                  disabled={isWeightDistanceBased && device.isStatic}
                />
              </Grid>
            </>
          )}

          {/* 日期欄位 */}
          <Grid size={6}>
            {device.dataTypeId === 4 ? (
              <DateRangePicker
                confirmedRange={confirmedRange}
                setConfirmedRange={setConfirmedRange}
                error={errors.dateRange}
                clearDateRangeError={() => clearError("dateRange")}
                year={store.selectedYear}
              />
            ) : (
              <CustomDatePicker
                label={t("Used Date")}
                value={usedDate}
                handleChange={handleDateChange}
                error={errors.usedDate}
                dataFormat={device.dataTypeId}
              />
            )}
          </Grid>

          {/* 數量欄位 */}
          <Grid size={device.calculationId === 4 ? 12 : 6}>
            {isFuelBased ? (
              <CustomTextField
                required
                label={t("Fuel")}
                unit={device.dataUnitName}
                value={consumptionValue}
                handleChange={(e) => {
                  setConsumptionValue(e.target.value);
                  clearError("consumptionValue");
                }}
                error={errors.consumptionValue}
              />
            ) : (
              <CustomTextField
                required={
                  !(isWeightDistanceBased || isDistanceBased || isCat3x)
                }
                label={t("Consumption Value /Quantity")}
                unit={device.dataUnitName}
                value={consumptionValue}
                handleChange={(e) => {
                  setConsumptionValue(e.target.value);
                  clearError("consumptionValue");
                }}
                error={errors.consumptionValue}
                disabled={
                  isWeightDistanceBased || isDistanceBased || isCat3x
                }
              />
            )}
          </Grid>

          {/* 月份重複錯誤提示 */}
          {isHelpText && (
            <Grid size={12}>
              <Typography color="red">
                {t(
                  "Data already exists. Each month can only have one data entry.",
                )}
              </Typography>
            </Grid>
          )}

          {/* cat 1_4：排放氣體資訊 */}
          {cat === "1_4" && (
            <Grid size={12}>
              <Typography>
                {t("Emission Gas Type") + " : "}
                {device.emissionFactorName || "-"}
              </Typography>

              {/* 計算方式：original filling amount */}
              {device.calculationMethodId == 2 && (
                <>
                  <Typography>
                    {t("Fugitive Emission Rate") + " : "}
                    {hasNumericValue(device.fugitiveEmissions)
                      ? `${device.fugitiveEmissions}%`
                      : "-"}
                  </Typography>
                  <Typography>
                    {t("Calculated Value") + " : "}
                    {hasNumericValue(device.fugitiveEmissions)
                      ? (device.fugitiveEmissions * consumptionValue) / 100
                      : "-"}
                    {" " + device.dataUnitName}
                  </Typography>
                </>
              )}

              {/* 計算方式：質量平衡法 */}
              {device.calculationMethodId == 3 && (
                <>
                  <Typography>
                    {t("% by mass") + " : "}
                    {device.value || "-"}
                  </Typography>
                  <Typography>
                    {t("Calculated Value") + " : "}
                    {device.value ? device.value * consumptionValue : "-"}
                    {" " + device.dataUnitName}
                  </Typography>
                </>
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => store._handleCloseDataEntryEditModal()}
          variant="contained"
          sx={{ bgcolor: "#e0e0e0", color: "#000", textTransform: "none" }}
        >
          {t("Close")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={isSubmitting || isDisabled}
          sx={{ textTransform: "none" }}
        >
          {isSubmitting ? t("Save...") : t("Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default DataEntryEditModal;
